import { lazy, Suspense } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import { ApiError } from './lib/api';
import { can, useSession } from './lib/session';
import { useI18n } from './lib/i18n';
import { Shell } from './components/Shell';
import { Alert, Logo } from './components/ui';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { AcceptCarPage } from './pages/AcceptCarPage';
import { CustomerPage } from './pages/CustomerPage';
import { FloorPage } from './pages/FloorPage';
import { MyWorkPage } from './pages/MyWorkPage';
import { PayrollPage } from './pages/PayrollPage';
import { CustomersPage } from './pages/CustomersPage';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { NewOrderPage } from './pages/NewOrderPage';
import { AuditPage } from './pages/AuditPage';
import { OrderPage } from './pages/OrderPage';
import { ImportPage } from './pages/ImportPage';
import { OrdersPage } from './pages/OrdersPage';
import { ReportsPage } from './pages/ReportsPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { VehiclePage } from './pages/VehiclePage';
import { WalletsPage } from './pages/WalletsPage';
import { FollowupsPage } from './pages/FollowupsPage';
import { RewardsPage } from './pages/RewardsPage';
import { ServicesPage } from './pages/ServicesPage';
import { SettingsPage } from './pages/SettingsPage';
import { StaffPage } from './pages/StaffPage';
import { TwoFactorSetupPage } from './pages/TwoFactorSetupPage';

// The QR decoder is a large dependency needed on one screen out of ten, so it
// is fetched only when that screen is opened.
const ScanPage = lazy(() =>
  import('./pages/ScanPage').then((module) => ({ default: module.ScanPage })),
);
// The calendar page; on a wide screen it fetches its week grid on top.
const CalendarPage = lazy(() =>
  import('./pages/CalendarPage').then((module) => ({ default: module.CalendarPage })),
);

const pageSpinner = (
  <div className="grid min-h-64 place-items-center">
    <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
  </div>
);

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center px-5">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <Logo withWordmark size="lg" />
        {children}
      </div>
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="font-display text-3xl font-bold tracking-tight text-ink">{t('notFound.title')}</h1>
      <p className="mt-2 text-sm text-ink-dim">
        {t('notFound.body')}
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex min-h-11 items-center rounded-xl border border-line bg-raised px-5 text-sm font-semibold text-ink transition-colors hover:border-brand/50"
      >
        {t('notFound.back')}
      </Link>
    </div>
  );
}

/**
 * Where the front door leads, which is not the same place for everyone.
 *
 * The summary is a money screen and most of the floor cannot open it, so
 * sending everyone there greeted a cashier with a locked page at the start of
 * every shift. Each role lands on the thing they actually came to do.
 */
function Home() {
  const { data: session } = useSession();
  if (can(session, 'reports.read')) return <DashboardPage />;
  if (can(session, 'orders.create')) return <Navigate to="/orders/new" replace />;
  // A technician's whole job is the list of cars on them.
  if (can(session, 'orders.work')) return <Navigate to="/my-work" replace />;
  return <Navigate to="/customers" replace />;
}

export function App() {
  const { t } = useI18n();
  const { data: session, isPending, error } = useSession();

  if (isPending) {
    return (
      <Splash>
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </Splash>
    );
  }

  if (error instanceof ApiError && error.code === 'PASSWORD_CHANGE_REQUIRED') {
    return <ChangePasswordPage forced />;
  }

  if (error instanceof ApiError && error.code === 'TWO_FACTOR_SETUP_REQUIRED') {
    return <TwoFactorSetupPage />;
  }

  if (error) {
    return (
      <Splash>
        <Alert>
          {t('app.apiUnreachable')}
          {error instanceof ApiError && error.requestId && (
            <span className="mt-1 block text-xs text-ink-faint">
              {t('app.requestId', { id: error.requestId })}
            </span>
          )}
        </Alert>
      </Splash>
    );
  }

  if (!session) return <LoginPage />;
  if (session.mustChangePassword) return <ChangePasswordPage forced />;
  if (session.twoFactorRequired) return <TwoFactorSetupPage />;

  return (
    <BrowserRouter>
      <Shell>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerPage />} />
          <Route path="/orders/new" element={<NewOrderPage />} />
          <Route path="/floor" element={<FloorPage />} />
          <Route path="/floor/accept" element={<AcceptCarPage />} />
          <Route path="/my-work" element={<MyWorkPage />} />
          <Route path="/calendar" element={<Suspense fallback={pageSpinner}><CalendarPage /></Suspense>} />
          <Route
            path="/scan"
            element={
              <Suspense fallback={
                <div className="grid min-h-64 place-items-center">
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                </div>
              }>
                <ScanPage />
              </Suspense>
            }
          />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:id" element={<OrderPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/imports" element={<ImportPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/payroll" element={<PayrollPage />} />
          <Route path="/vehicles" element={<VehiclesPage />} />
          <Route path="/vehicles/:id" element={<VehiclePage />} />
          <Route path="/wallets" element={<WalletsPage />} />
          <Route path="/followups" element={<FollowupsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/settings/password" element={<ChangePasswordPage forced={false} />} />
          {/* Signing in from a bookmarked /login leaves the browser on a path
              that no longer exists once there is a session. */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Shell>
    </BrowserRouter>
  );
}
